import { useState } from 'preact/hooks'
import { startRegistration } from '@simplewebauthn/browser'
import { api, ApiError, deviceHint, type Me } from '../api.js'

/**
 * Einladung annehmen — der erste Kontakt mit der App.
 *
 * Zwei Schritte, nicht mehr: Name eintragen, dann Passkey einrichten. Der
 * Passkey-Schritt ist überspringbar, weil ein Gerät ohne WebAuthn sonst in einer
 * Sackgasse stünde (Anf. 72). Die App bittet später erneut darum.
 */
export function Einladung({ token, onDone }: { token: string; onDone: (me: Me) => void }) {
  const [schritt, setSchritt] = useState<'name' | 'passkey'>('name')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  async function beitreten(e: Event) {
    e.preventDefault()
    setFehler(null)
    setBusy(true)
    try {
      await api.post(`/invites/${encodeURIComponent(token)}/accept`, {
        displayName: name.trim(), device: deviceHint(),
      })
      setSchritt('passkey')
    } catch (e) {
      setFehler(
        e instanceof ApiError && e.status === 404
          ? 'Diese Einladung ist abgelaufen oder wurde schon benutzt. Bitte um eine neue.'
          : 'Das hat nicht geklappt. Versuch es noch einmal.',
      )
    } finally {
      setBusy(false)
    }
  }

  async function passkeyEinrichten() {
    setFehler(null)
    setBusy(true)
    try {
      const options = await api.post<Parameters<typeof startRegistration>[0]['optionsJSON']>(
        '/auth/webauthn/register/options',
      )
      const response = await startRegistration({ optionsJSON: options })
      await api.post('/auth/webauthn/register/verify', {
        response, label: deviceHint().name,
      })
      onDone(await api.get<Me>('/me'))
    } catch {
      setFehler('Das hat nicht geklappt. Du kannst es später in den Einstellungen nachholen.')
    } finally {
      setBusy(false)
    }
  }

  if (schritt === 'name') {
    return (
      <div class="fh-anmelden">
        <h1 class="fh-h1">Du bist eingeladen</h1>
        <p class="fh-muted">Wie sollen dich die anderen sehen?</p>

        <form class="fh-card fh-anmelden__formular" onSubmit={(e) => void beitreten(e)}>
          <label class="fh-feld">
            <span>Dein Name</span>
            <input
              type="text" required autofocus maxLength={60} value={name}
              placeholder="zum Beispiel: Oma"
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
            />
          </label>
          {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
          <button class="fh-btn" type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Einen Moment…' : 'Weiter'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div class="fh-anmelden">
      <h1 class="fh-h1">Fast fertig, {name}</h1>
      <p class="fh-muted">
        Damit du dich künftig ohne Passwort anmelden kannst, richten wir jetzt
        deinen Fingerabdruck oder dein Gesicht ein.
      </p>

      {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

      <button class="fh-btn fh-btn--gross" onClick={() => void passkeyEinrichten()} disabled={busy}>
        <span aria-hidden="true">👆</span>
        {busy ? 'Einen Moment…' : 'Jetzt einrichten'}
      </button>

      <button
        class="fh-btn fh-btn--quiet fh-anmelden__alt"
        onClick={() => { void api.get<Me>('/me').then(onDone) }}
        disabled={busy}
      >
        Später einrichten
      </button>
    </div>
  )
}
